import { AiKeyMetadata } from '../../api';
import { BaseButton } from '../base/button/button';
import { BaseInput } from '../base/input/input';
import { mdiMagnify } from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { minutesLeft } from '../../services/ai/ai.derive';
import { AiStore } from '../../stores/ai/ai.store';
import { formatWhen, KEY_STATUS_TONE, StatusTag, TargetTag } from './ai-fields';
import * as S from './ai.styles';

const KEY_COLUMNS = '130px minmax(110px,1fr) 80px minmax(170px,1.4fr) minmax(130px,1fr) minmax(130px,1fr) 100px 86px';

interface IKeysTabProps {
    store: AiStore;
    onRevoke: (key: AiKeyMetadata) => void;
}

export const KeysTab = observer(({ store, onRevoke }: IKeysTabProps): React.ReactElement => {
    const [lookupId, setLookupId] = useState('');

    const lookup = async (): Promise<void> => {
        await store.lookupKey(lookupId);
        if (!store.lookupMessage) setLookupId('');
    };

    return (
        <S.Stack>
            <S.Card>
                <S.CardTitle>Look up key</S.CardTitle>
                <S.InlineRow>
                    <S.Grow>
                        <BaseInput
                            aria-label="Key ID"
                            value={lookupId}
                            placeholder="Enter key ID (uuid), e.g. d743966e-9eef-4c5a-9eb4-329119f5639c"
                            onChange={(e): void => {
                                setLookupId(e.target.value);
                                store.clearLookupMessage();
                            }}
                            onKeyDown={(e): void => {
                                if (e.key === 'Enter') void lookup();
                            }}
                        />
                    </S.Grow>
                    <S.Button variant="outlined" onClick={(): void => void lookup()}>
                        <S.ButtonContent>
                            <Icon path={mdiMagnify} size={0.75} />
                            Look up
                        </S.ButtonContent>
                    </S.Button>
                </S.InlineRow>
                {store.lookupMessage && <S.ErrorText>{store.lookupMessage}</S.ErrorText>}
                <S.Hint>
                    There is no list endpoint. Keys issued here or found by ID are tracked below for this session. Metadata
                    never includes the secret or gateway hash.
                    {store.mode === 'provider' && ' You can look up and revoke only keys you issued yourself.'}
                </S.Hint>
            </S.Card>

            <S.Table>
                <S.TableHead>
                    <S.CardTitle>Tracked keys</S.CardTitle>
                    <S.Muted>This session only — not every key on the account.</S.Muted>
                </S.TableHead>
                <S.Scroll>
                    <S.Row header={true} columns={KEY_COLUMNS} min={960}>
                        <span>Key ID</span>
                        <span>Profile</span>
                        <span>Type</span>
                        <span>Default model</span>
                        <span>Created</span>
                        <span>Expires</span>
                        <span>Status</span>
                        <span />
                    </S.Row>
                    {store.tracked.map(k => {
                        const status = store.statusOf(k);
                        return (
                            <S.Row key={k.id} columns={KEY_COLUMNS} min={960}>
                                <S.Mono title={k.id}>{k.id.slice(0, 8)}…</S.Mono>
                                <S.Mono>{k.profileId}</S.Mono>
                                <span>
                                    <TargetTag type={k.targetType} />
                                </span>
                                <S.Mono>{k.defaultModel}</S.Mono>
                                <span>{formatWhen(k.createdAt)}</span>
                                <S.Cell>
                                    <span>{formatWhen(k.expiresAt)}</span>
                                    {status === 'ACTIVE' && <S.Muted>{minutesLeft(k, store.now)} min left</S.Muted>}
                                </S.Cell>
                                <span>
                                    <StatusTag tone={KEY_STATUS_TONE[status]}>{status}</StatusTag>
                                </span>
                                <span>
                                    {status === 'ACTIVE' && (
                                        <BaseButton variant="outlined" size="compact" onClick={(): void => onRevoke(k)}>
                                            Revoke
                                        </BaseButton>
                                    )}
                                </span>
                            </S.Row>
                        );
                    })}
                    {store.tracked.length === 0 && <S.Empty>No keys tracked. Issue a key or look one up by ID.</S.Empty>}
                </S.Scroll>
            </S.Table>
        </S.Stack>
    );
});
